import get from 'lodash/get';

import { ACTION_TYPES as RESTAURANT_ACTION_TYPES, ROUTE_TYPES_VALUES } from 'src/modules/Restaurant/constants';
import { ACTION_TYPES as MENU_ACTION_TYPES } from 'src/modules/Menu/constants';
import { ACTION_TYPES, BILL_STATUS, ORDER_STATUS } from './constants';
import { calculatePrice, prepareConfigurablePosition, createOrderNotification, createBillNotification } from './utils';

const initialState = {
    orders: [],
    sessionSummary: null,
    areOrdersFetched: false,
    isOrderPositionAdded: false,
    isOrderPositionAdding: false,
    isOrderPositionRemoved: false,
    context: null,
    addOrderPositionError: null,
    isSubmitOrderPending: false,
    configurablePosition: null,
    validationErrors: [],
    isValid: true,
    price: 0,
    positionsNumber: 0,
    isAskForBillPending: false,
    clientNameCallingWaiter: null,
    clientNameClosingBill: null,
    fingerprintCallingWaiter: null,
    fingerprintClosingBill: null,
    notification: null,
    lastBillPaid: false,
    reinitialize: false,
    sessionError: false,
    canCreateOrders: false,
    canMakePayment: false,
    externalPayment: null,
    submitOrderError: null,
    cancelOrderError: null,
    repaymentError: null,
    askForBillError: null
};

export default function menuReducer(state = initialState, action) {
    switch (action.type) {
        case RESTAURANT_ACTION_TYPES.RESTAURANT_INITIALIZED: {
            const {
                restaurant: {
                    id,
                    isOpen,
                    menuPositionConfiguration: {
                        imageShape,
                        showAllergens,
                        showTags,
                        showDescription
                    },
                    name
                },
                tableId,
                id: sessionId,
                routeType,
                tableName,
                tableNumber
            } = action.payload;

            return {
                ...state,
                isInitialized: true,
                context: {
                    restaurantId: id,
                    isRestaurantOpen: isOpen,
                    menuPositionImageShape: imageShape,
                    restaurantName: name,
                    showAllergens,
                    showTags,
                    showDescription,
                    sessionId,
                    tableId,
                    routeType,
                    tableNumber,
                    tableName
                },
                lastBillPaid: routeType !== ROUTE_TYPES_VALUES.BILL_WAIT
            };
        }

        case ACTION_TYPES.BILL_FETCHED: {
            const lastBillPaid = action.payload.bill.status === BILL_STATUS.PAID;

            const notification = createBillNotification(
                state.lastBillPaid,
                lastBillPaid,
                state.isInitialized
            );

            const reinitialize = notification !== null;

            return {
                ...state,
                lastBillPaid,
                notification,
                reinitialize
            };
        }

        case ACTION_TYPES.INCREASE_QUANTITY: {
            return {
                ...state,
                configurablePosition: {
                    ...state.configurablePosition,
                    quantity: state.configurablePosition.quantity + 1
                }
            };
        }

        case ACTION_TYPES.SESSION_ERROR_FETCHED: {
            return {
                ...state,
                sessionError: true
            };
        }

        case ACTION_TYPES.DECREASE_QUANTITY: {
            return {
                ...state,
                configurablePosition: {
                    ...state.configurablePosition,
                    quantity: state.configurablePosition.quantity <= 1
                        ? state.configurablePosition.quantity
                        : state.configurablePosition.quantity - 1
                }
            };
        }

        case ACTION_TYPES.SET_SELECTED_MENU_POSITION: {
            return {
                ...state,
                configurablePosition: {
                    ...action.payload,
                    quantity: 1
                },
                validationErrors: initialState.validationErrors
            };
        }

        case ACTION_TYPES.SELECTED_MENU_POSITION_CLEARED: {
            return {
                ...state,
                configurablePosition: initialState.configurablePosition,
                isOrderPositionAdded: initialState.isOrderPositionAdded
            };
        }

        case ACTION_TYPES.ORDER_POSITION_ADDED: {
            return {
                ...state,
                isOrderPositionAdded: true
            };
        }

        case ACTION_TYPES.ADD_ORDER_POSITION_FAILED: {
            return {
                ...state,
                addOrderPositionError: action.payload || 'canNotAddPositionToOrder',
                isOrderPositionAdded: false
            };
        }

        case ACTION_TYPES.CLEAR_ADD_ORDER_POSITION_ERROR_MESSAGE: {
            return {
                ...state,
                addOrderPositionError: initialState.addOrderPositionError,
                isOrderPositionAdded: false,
                validationErrors: initialState.validationErrors
            };
        }

        case ACTION_TYPES.ORDERS_FETCHED: {
            const {
                data: {
                    positionsNumber,
                    cart,
                    orders,
                    price,
                    clientNameCallingWaiter,
                    clientNameClosingBill,
                    fingerprintCallingWaiter,
                    fingerprintClosingBill,
                    sessionSummary,
                    canCreateOrders,
                    canMakePayment
                },
                fingerprint
            } = action.payload;

            const ordersWithCart = [
                ...cart.positions.length
                    ? [{
                        positions: cart.positions,
                        status: ORDER_STATUS.NEW,
                        summary: cart.amount
                    }]
                    : [],
                ...orders
            ];

            const notification = createOrderNotification(
                state.orders,
                ordersWithCart,
                state.fingerprintCallingWaiter,
                state.fingerprintClosingBill,
                clientNameCallingWaiter,
                clientNameClosingBill,
                fingerprintCallingWaiter,
                fingerprintClosingBill,
                fingerprint,
                state.selectAreOrdersFetched
            );

            return {
                ...state,
                orders: ordersWithCart,
                sessionSummary,
                price,
                positionsNumber,
                canCreateOrders,
                clientNameCallingWaiter,
                clientNameClosingBill,
                fingerprintCallingWaiter,
                fingerprintClosingBill,
                selectAreOrdersFetched: true,
                notification,
                canMakePayment
            };
        }

        case ACTION_TYPES.SUBMIT_ORDER_PENDING: {
            return {
                ...state,
                isSubmitOrderPending: true,
                externalPayment: null,
                submitOrderError: null
            };
        }

        case ACTION_TYPES.SUBMIT_ORDER_SUCCESS: {
            return {
                ...state,
                isSubmitOrderPending: false,
                externalPayment: action.payload,
                submitOrderError: null
            };
        }

        case ACTION_TYPES.SUBMIT_ORDER_FAILED: {
            return {
                ...state,
                isSubmitOrderPending: false,
                externalPayment: null,
                submitOrderError: action.payload || 'cannotPlaceOrder'
            };
        }

        case ACTION_TYPES.CLEAR_SUBMIT_ORDER_ERROR: {
            return {
                ...state,
                isSubmitOrderPending: false,
                submitOrderError: initialState.submitOrderError
            };
        }

        case ACTION_TYPES.SUBMIT_ASK_FOR_BILL: {
            return {
                ...state,
                isAskForBillPending: true
            };
        }

        case ACTION_TYPES.ASKED_FOR_BILL: {
            return {
                ...state,
                isAskForBillPending: false
            };
        }

        case ACTION_TYPES.SUBMIT_ASK_FOR_BILL_FAILED: {
            return {
                ...state,
                isAskForBillPending: false,
                askForBillError: action.payload
            };
        }

        case MENU_ACTION_TYPES.MENU_POSITION_DETAILS_FETCHED: {
            return {
                ...state,
                configurablePosition: {
                    ...state.configurablePosition,
                    ...action.payload,
                    quantity: state.configurablePosition.quantity
                }
            };
        }

        case ACTION_TYPES.SET_ADDON_AS_SELECTED: {
            const { virtualId, quantity } = action.payload;

            const selectAddonRecursive = (position, group) => {
                let shouldOverrideSelected = false;

                if (group && group.addons.some((addon) => addon.virtualId === virtualId)) {
                    shouldOverrideSelected = true;
                }

                let canChangeQuantity = false;
                if (virtualId === position.virtualId
                    && quantity !== undefined
                    && quantity >= 0
                    && group
                    && group.controlType !== 'radio'
                    && shouldOverrideSelected
                    && group.maxQuantity >= group.addons.reduce(
                        (result, addon) => result + (virtualId === addon.virtualId ? quantity : addon.quantity),
                        0
                    )
                ) {
                    canChangeQuantity = true;
                }

                return ({
                    ...position,
                    quantity: virtualId === position.virtualId && canChangeQuantity
                        ? quantity
                        : position.quantity,
                    addonGroups: position.addonGroups.map((addonGroup) => {
                        shouldOverrideSelected = false;
                        if (addonGroup
                            && addonGroup.controlType === 'radio'
                            && addonGroup.addons.some((addon) => addon.virtualId === virtualId)) {
                            shouldOverrideSelected = true;
                        }

                        return ({
                            ...addonGroup,
                            selectedAddon: shouldOverrideSelected
                                ? get(addonGroup.addons.find((addon) => addon.virtualId === virtualId), 'virtualId', undefined)
                                : addonGroup.selectedAddon,
                            addons: addonGroup.addons.map((addon) => selectAddonRecursive(addon, addonGroup))
                        });
                    })
                });
            };

            const configurablePosition = selectAddonRecursive(state.configurablePosition);

            const calculatedPrice = calculatePrice(configurablePosition);

            return {
                ...state,
                configurablePosition: {
                    ...configurablePosition,
                    quantity: state.configurablePosition.quantity,
                    calculatedPrice
                }
            };
        }

        case ACTION_TYPES.ADD_ADDON: {
            const virtualId = action.payload;

            const addAddonRecursive = (addonGroups = []) => addonGroups.map((addonGroup) => {
                if (addonGroup && addonGroup.addons.some((addon) => addon.virtualId === virtualId)) {
                    return {
                        ...addonGroup,
                        addons: [
                            ...addonGroup.addons,
                            {
                                ...prepareConfigurablePosition(addonGroup.addons.find((addon) => addon.virtualId === virtualId)),
                                quantity: 1
                            }
                        ]
                    };
                }

                return {
                    ...addonGroup,
                    addons: addonGroup.addons.map((addon) => ({
                        ...addon,
                        addonGroups: addAddonRecursive(addon.addonGroups)
                    }))
                };
            });

            const configurablePosition = {
                ...state.configurablePosition,
                addonGroups: addAddonRecursive(state.configurablePosition.addonGroups)
            };

            const calculatedPrice = calculatePrice(configurablePosition);

            return {
                ...state,
                configurablePosition: {
                    ...configurablePosition,
                    quantity: state.configurablePosition.quantity,
                    calculatedPrice
                }
            };
        }

        case ACTION_TYPES.REMOVE_ADDON: {
            const { virtualId, position } = action.payload;

            const prepareConfigurablePositionGroups = (addonGroups = []) => addonGroups.map((addonGroup) => {
                if (addonGroup && addonGroup.addons.some((addon) => addon.virtualId === virtualId
                    && addon.quantity > 0)) {
                    return {
                        ...addonGroup,
                        addons: addonGroup.addons.filter((addon, index) => position !== index)
                    };
                }

                return {
                    ...addonGroup,
                    addons: addonGroup.addons.map((addon) => ({
                        ...addon,
                        addonGroups: prepareConfigurablePositionGroups(addon.addonGroups)
                    }))
                };
            });

            const configurablePosition = {
                ...state.configurablePosition,
                addonGroups: prepareConfigurablePositionGroups(state.configurablePosition.addonGroups)
            };

            const calculatedPrice = calculatePrice(configurablePosition);

            return {
                ...state,
                configurablePosition: {
                    ...configurablePosition,
                    quantity: state.configurablePosition.quantity,
                    calculatedPrice
                }
            };
        }

        case ACTION_TYPES.ORDER_POSITION_VALIDATED: {
            const errors = action.payload;

            return {
                ...state,
                validationErrors: errors,
                isValid: Boolean(errors.length)
            };
        }

        case ACTION_TYPES.REMOVE_ORDER_POSITION: {
            return {
                ...state,
                isSubmitOrderPending: false,
                isOrderPositionRemoved: true
            };
        }

        case ACTION_TYPES.REMOVE_ORDER_POSITION_FAILED: {
            return {
                ...state,
                isSubmitOrderPending: false,
                addOrderPositionError: initialState.addOrderPositionError || 'canNotRemovePositionFromOrder',
                isOrderPositionRemoved: false
            };
        }

        case ACTION_TYPES.CHANGE_QUANTITY_ORDER_POSITION: {
            const { positionId, quantity } = action.payload;
            return {
                ...state,
                orders: state.orders.map((order, index) => {
                    if (index === 0) {
                        return {
                            ...order,
                            positions: order.positions.map((position) => {
                                if (position.id === positionId) {
                                    return {
                                        ...position,
                                        quantity
                                    };
                                }

                                return position;
                            })
                        };
                    }

                    return order;
                })
            };
        }

        case ACTION_TYPES.COMMENT_CHANGED: {
            return {
                ...state,
                configurablePosition: {
                    ...state.configurablePosition,
                    comment: action.payload
                }
            };
        }

        case ACTION_TYPES.REPAYMENT_SUCCESS: {
            return {
                ...state,
                externalPayment: action.payload
            };
        }

        case ACTION_TYPES.REPAYMENT_FAILED: {
            return {
                ...state,
                repaymentError: action.payload || 'repaymentFailed'
            };
        }

        case ACTION_TYPES.CLEAR_REPAYMENT_ERROR: {
            return {
                ...state,
                repaymentError: initialState.repaymentError
            };
        }

        case ACTION_TYPES.CANCEL_ORDER_FAILED: {
            return {
                ...state,
                cancelOrderError: action.payload || 'cancelOrderFailed'
            };
        }

        case ACTION_TYPES.CLEAR_CANCEL_ORDER_ERROR: {
            return {
                ...state,
                cancelOrderError: initialState.cancelOrderError
            };
        }

        case ACTION_TYPES.CLEAR_ASK_FOR_BILL_ERROR: {
            return {
                ...state,
                askForBillError: initialState.askForBillError
            };
        }

        default: return state;
    }
}
